import { ComponentPropsWithoutRef, MouseEvent } from 'react';

import connectFieldsStyles from '@components/primitives/ConnectedFields/styles.module.css';
import { useFormField } from '@components/primitives/FormField';
import { cn } from '@components/primitives/utilities';

import { focusControl } from './focusControl';
import { InputGroupAddon } from './InputGroupAddon';
import { InputGroupButton } from './InputGroupButton';
import { InputGroupInput } from './InputGroupInput';
import { InputGroupText } from './InputGroupText';

import css from './styles.module.css';

export interface InputGroupProps extends ComponentPropsWithoutRef<'div'> {
  error?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Addons place themselves with CSS `order`, so write the control first and DOM
 * order stays the reading and tab order.
 */
export function InputGroup({
  className,
  error,
  onClick,
  size = 'md',
  ...props
}: InputGroupProps) {
  const formField = useFormField();

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    onClick?.(event);
    focusControl(event, event.currentTarget);
  }

  return (
    <div
      role="group"
      onClick={handleClick}
      className={cn(connectFieldsStyles.connectableField, css.group, className)}
      data-size={size}
      data-error={error ?? Boolean(formField?.error)}
      data-variant="default"
      {...props}
    />
  );
}

InputGroup.Addon = InputGroupAddon;
InputGroup.Button = InputGroupButton;
InputGroup.Input = InputGroupInput;
InputGroup.Text = InputGroupText;

export type {
  InputGroupAddonAlign,
  InputGroupAddonProps,
} from './InputGroupAddon';
